// `useCurrentUser()` — a tiny hook reading the `['me']` query for any
// component that used to consume `user` via prop-drilling. Returns
// `{ user, isLoading, isAuthenticated }`. Use `useAuth()` when you also
// need login/logout/modal helpers; prefer this hook when you only want
// to read the current user to avoid pulling in the modal state.

import { useQuery } from '@tanstack/react-query';
import { authKeys } from '../../shared/api/queryKeys';
import type { UserData } from '../../shared/types';
import { fetchMe } from './authApi';

export interface CurrentUserState {
  user: UserData | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export function useCurrentUser(): CurrentUserState {
  // The AuthProvider mounts the `['me']` query on tree init. Subscribing
  // here just mirrors its cache — no extra network request.
  const query = useQuery({
    queryKey: authKeys.me(),
    queryFn: fetchMe,
    // Never surface a throwing state here — `fetchMe` normalises 401 to
    // `null`, and anything else is a network/5xx that the AuthProvider
    // has already logged.
    retry: 0,
  });
  return {
    user: query.data ?? null,
    isLoading: query.isPending,
    isAuthenticated: !!query.data,
  };
}
